import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';


const pad = (value) => String(value).padStart(2, '0');

const getDefaultTime = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 2);
  return { hour: now.getHours(), minute: now.getMinutes() };
};

const parseTime = (value) => {
  const match = /^(\d{2}):(\d{2})$/.exec(value || '');
  if (!match) return getDefaultTime();

  const hour24 = Number(match[1]);
  const minute = Number(match[2]);
  return { hour: hour24, minute };
};

const toTimeValue = (hour, minute) => `${pad(hour)}:${pad(minute)}`;

const formatDisplayTime = (value) => {
  if (!value) return '';
  const { hour, minute } = parseTime(value);
  return `${pad(hour)}:${pad(minute)}`;
};

function SingleTimePicker({
  id,
  name,
  value,
  onChange,
  className = 'ui-input',
  placeholder = '--:--',
  disabled = false,
  minTime,
  style,
}) {
  const wrapperRef = useRef(null);
  const hourListRef = useRef(null);
  const minuteListRef = useRef(null);
  const parsedValue = useMemo(() => parseTime(value), [value]);
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(parsedValue);

  useEffect(() => {
    setDraft(parsedValue);
  }, [parsedValue]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const scrollSelectedIntoView = (ref) => {
      ref.current?.querySelector('.is-selected')?.scrollIntoView({
        block: 'center',
        behavior: 'auto',
      });
    };

    requestAnimationFrame(() => {
      scrollSelectedIntoView(hourListRef);
      scrollSelectedIntoView(minuteListRef);
    });
  }, [isOpen, draft.hour, draft.minute]);

  const emitChange = (nextDraft) => {
    onChange?.({
      target: {
        id,
        name: name || id,
        value: toTimeValue(nextDraft.hour, nextDraft.minute),
      },
    });
  };

  const updateDraft = (patch) => {
    const nextDraft = { ...draft, ...patch };
    setDraft(nextDraft);
    emitChange(nextDraft);
  };

  const minBound = useMemo(() => {
    const match = /^(\d{2}):(\d{2})$/.exec(minTime || '');
    if (!match) return null;
    return { hour: Number(match[1]), minute: Number(match[2]) };
  }, [minTime]);

  const isHourDisabled = (hour) => Boolean(minBound && hour < minBound.hour);
  const isMinuteDisabled = (minute) =>
    Boolean(
      minBound &&
        (draft.hour < minBound.hour ||
          (draft.hour === minBound.hour && minute < minBound.minute))
    );

  const hours = Array.from({ length: 24 }, (_, index) => index);
  const minutes = Array.from(new Set([
    ...Array.from({ length: 60 }, (_, index) => index),
    draft.minute,
  ])).sort((a, b) => a - b);

  return (
    <div className="tw-relative tw-w-full single-time-picker" ref={wrapperRef}>
      <div className={"tw-relative [&_input]:tw-cursor-pointer [&_input]:tw-pr-[44px] single-time-picker__field"}>
        <input
          type="text"
          id={id}
          name={name || id}
          value={formatDisplayTime(value)}
          readOnly
          disabled={disabled}
          className={className}
          style={style}
          placeholder={placeholder}
          autoComplete="off"
          onClick={() => !disabled && setIsOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsOpen((open) => !open);
            }
            if (event.key === 'Escape') {
              setIsOpen(false);
            }
          }}
        />
        <button
          type="button"
          className={"tw-absolute tw-top-[50%] tw-right-[12px] [transform:translateY(-50%)] tw-w-[28px] tw-h-[28px] tw-border-0 tw-rounded-[50%] tw-text-[#111827] tw-bg-[transparent] tw-inline-flex tw-items-center tw-justify-center tw-text-[20px] tw-leading-[1] [&:hover:not(:disabled)]:tw-bg-[#eef4ff] [&:hover:not(:disabled)]:tw-text-[#2554f4] [&:disabled]:tw-text-[#9ca3af] [&:disabled]:tw-cursor-not-allowed single-time-picker__trigger"}
          onClick={() => !disabled && setIsOpen((open) => !open)}
          disabled={disabled}
          aria-label="Open time picker"
        >
          <Icon icon="mdi:clock-outline" />
        </button>
      </div>

      {isOpen && (
        <div className={"tw-absolute tw-left-0 tw-bottom-[calc(100%_+_10px)] tw-z-[1065] tw-w-[min(220px,_calc(100vw_-_32px))] tw-p-[8px] tw-border-[1px] tw-border-solid tw-border-[#d7dce5] tw-rounded-[8px] tw-bg-[#ffffff] tw-shadow-[0_18px_45px_rgba(15,_23,_42,_0.18)] [&::after]:[content:''] [&::after]:tw-absolute [&::after]:tw-right-[22px] [&::after]:tw-bottom-[-8px] [&::after]:tw-w-[14px] [&::after]:tw-h-[14px] [&::after]:tw-border-r-[1px] [&::after]:tw-border-r-[#d7dce5] [&::after]:tw-border-b-[1px] [&::after]:tw-border-solid [&::after]:tw-border-b-[#d7dce5] [&::after]:tw-bg-[#ffffff] [&::after]:[transform:rotate(45deg)] max-[480px]:tw-w-[min(220px,_calc(100vw_-_24px))] single-time-picker__popover"} role="dialog" aria-label="Choose time">
          <div className="tw-min-h-[30px] tw-border-[1px] tw-border-solid tw-border-[#edf0f5] tw-rounded-[6px] tw-bg-[#f8fafc] tw-text-[#111827] tw-flex tw-items-center tw-justify-center tw-text-[13px] tw-font-bold tw-mb-[8px] single-time-picker__title">Select Time</div>

          <div className="tw-grid tw-grid-cols-[1fr_1fr] tw-gap-[8px] single-time-picker__columns">
            <div className={"[&_>_span]:tw-block [&_>_span]:tw-pb-[5px] [&_>_span]:tw-border-b-[1px] [&_>_span]:tw-border-solid [&_>_span]:tw-border-b-[#e5e7eb] [&_>_span]:tw-text-[#4b5563] [&_>_span]:tw-text-[12px] [&_>_span]:tw-font-bold [&_>_span]:tw-text-center single-time-picker__column"}>
              <span>Hour</span>
              <div className={"tw-relative tw-flex tw-flex-col tw-gap-[3px] tw-h-[176px] tw-mt-[6px] tw-pt-[6px] tw-pb-[6px] tw-overflow-y-auto [scroll-snap-type:y_mandatory] [scrollbar-width:none] tw-rounded-[7px] [background:linear-gradient(#ffffff,_#ffffff)_padding-box,_linear-gradient(to_bottom,_transparent,_#eef2f7,_transparent)_border-box] [&::-webkit-scrollbar]:tw-hidden [&_button]:[flex:0_0_28px] [&_button]:tw-min-w-[0] [&_button]:tw-min-h-[28px] [&_button]:tw-border-[1px] [&_button]:tw-border-solid [&_button]:tw-border-[transparent] [&_button]:tw-rounded-[6px] [&_button]:tw-bg-[transparent] [&_button]:tw-text-[#111827] [&_button]:tw-text-[12px] [&_button]:tw-font-bold [&_button]:[scroll-snap-align:center] [&_button:hover]:tw-border-[#94b7ff] [&_button:hover]:tw-bg-[#eef4ff] [&_button:hover]:tw-text-[#2554f4] [&_button.is-selected]:tw-border-[#2554f4] [&_button.is-selected]:tw-bg-[#2554f4] [&_button.is-selected]:tw-text-[#ffffff] [&_button.is-selected]:tw-shadow-[0_5px_12px_rgba(37,_84,_244,_0.22)] [&_button.is-disabled]:tw-text-[#cbd1da] [&_button.is-disabled]:tw-cursor-not-allowed [&_button.is-disabled]:tw-pointer-events-none [&_button:disabled]:tw-text-[#cbd1da] [&_button:disabled]:tw-cursor-not-allowed [&_button:disabled]:tw-pointer-events-none single-time-picker__options"} role="listbox" aria-label="Hour" ref={hourListRef}>
                {hours.map((hour) => (
                  <button
                    type="button"
                    key={hour}
                    className={[
                      hour === draft.hour ? "is-selected" : "",
                      isHourDisabled(hour) ? "is-disabled" : "",
                    ].filter(Boolean).join("      ")}
                    disabled={isHourDisabled(hour)}
                    onClick={() => updateDraft({ hour })}
                  >
                    {pad(hour)}
                  </button>
                ))}
              </div>
            </div>

            <div className={"[&_>_span]:tw-block [&_>_span]:tw-pb-[5px] [&_>_span]:tw-border-b-[1px] [&_>_span]:tw-border-solid [&_>_span]:tw-border-b-[#e5e7eb] [&_>_span]:tw-text-[#4b5563] [&_>_span]:tw-text-[12px] [&_>_span]:tw-font-bold [&_>_span]:tw-text-center single-time-picker__column"}>
              <span>Min</span>
              <div className={"tw-relative tw-flex tw-flex-col tw-gap-[3px] tw-h-[176px] tw-mt-[6px] tw-pt-[6px] tw-pb-[6px] tw-overflow-y-auto [scroll-snap-type:y_mandatory] [scrollbar-width:none] tw-rounded-[7px] [background:linear-gradient(#ffffff,_#ffffff)_padding-box,_linear-gradient(to_bottom,_transparent,_#eef2f7,_transparent)_border-box] [&::-webkit-scrollbar]:tw-hidden [&_button]:[flex:0_0_28px] [&_button]:tw-min-w-[0] [&_button]:tw-min-h-[28px] [&_button]:tw-border-[1px] [&_button]:tw-border-solid [&_button]:tw-border-[transparent] [&_button]:tw-rounded-[6px] [&_button]:tw-bg-[transparent] [&_button]:tw-text-[#111827] [&_button]:tw-text-[12px] [&_button]:tw-font-bold [&_button]:[scroll-snap-align:center] [&_button:hover]:tw-border-[#94b7ff] [&_button:hover]:tw-bg-[#eef4ff] [&_button:hover]:tw-text-[#2554f4] [&_button.is-selected]:tw-border-[#2554f4] [&_button.is-selected]:tw-bg-[#2554f4] [&_button.is-selected]:tw-text-[#ffffff] [&_button.is-selected]:tw-shadow-[0_5px_12px_rgba(37,_84,_244,_0.22)] [&_button.is-disabled]:tw-text-[#cbd1da] [&_button.is-disabled]:tw-cursor-not-allowed [&_button.is-disabled]:tw-pointer-events-none [&_button:disabled]:tw-text-[#cbd1da] [&_button:disabled]:tw-cursor-not-allowed [&_button:disabled]:tw-pointer-events-none single-time-picker__options"} role="listbox" aria-label="Minute" ref={minuteListRef}>
                {minutes.map((minute) => (
                  <button
                    type="button"
                    key={minute}
                    className={[
                      minute === draft.minute ? "is-selected" : "",
                      isMinuteDisabled(minute) ? "is-disabled" : "",
                    ].filter(Boolean).join("      ")}
                    disabled={isMinuteDisabled(minute)}
                    onClick={() => updateDraft({ minute })}
                  >
                    {pad(minute)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className={"tw-flex tw-justify-between tw-pt-[8px] [&_button]:tw-border-0 [&_button]:tw-bg-[transparent] [&_button]:tw-text-[#2554f4] [&_button]:tw-text-[13px] [&_button]:tw-font-bold [&_button]:tw-p-[3px_6px] [&_button:hover]:tw-underline single-time-picker__footer"}>
            <button type="button" onClick={() => updateDraft(parseTime('09:00'))}>09:00</button>
            <button type="button" onClick={() => setIsOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SingleTimePicker;
